import { Button, InputNumber, Modal, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { svg } from "../../../../Assets/svg";
import { v4 as uuidv4 } from "uuid";
import {
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { S3FileUploader } from "../../../../Components/S3FileUploader";
import { getFullAwsUrl } from "../../../../helpers/utils";
import CommonFilePreviewModal from "../../../Components/CommonFilePreviewModal";

interface File {
  _id: string;
  key: string;
  fileName: string;
}

interface DesignGroup {
  _id: string;
  qty: number;
  files: File[];
}

interface UploadedCustomDrawing {
  document: string;
  fileName: string;
}

const getFileNameFromUrl = (key: string) => {
  if (!key) return "";
  try {
    const parts = key.split("/");
    const fullName = parts[parts.length - 1];
    if (fullName.includes("_")) {
      return fullName.substring(fullName.indexOf("_") + 1);
    }
    return fullName;
  } catch (e) {
    return key;
  }
};

const ConfigureDrawingsModal = ({
  open,
  onClose,
  drawingsData,
  partsData,
  componentsData,
  assemblyData,
  fullDrawingData,
  quotationId,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  drawingsData?: any;
  partsData?: any;
  componentsData?: any;
  assemblyData?: any;
  fullDrawingData?: any;
  quotationId: string;
  onSubmit: (payload: any, setLoader: any) => void;
}) => {
  const variantsList =
    drawingsData || fullDrawingData?.variants_requiring_drawing || [];
  const partsList =
    partsData || fullDrawingData?.parts_requiring_drawing || [];
  const componentsList =
    componentsData || fullDrawingData?.components_requiring_drawing || [];
  const assemblyList =
    assemblyData || fullDrawingData?.assembly_drawings_requiring_drawing || [];

  const [allocations, setAllocations] = useState<Record<string, DesignGroup[]>>(
    {},
  );
  const [uploadStates, setUploadStates] = useState<Record<string, number>>({});

  // Parts custom drawings state: { [part_id]: { [drawing_id_or_title]: { document, fileName } } }
  const [partDrawings, setPartDrawings] = useState<
    Record<string, Record<string, UploadedCustomDrawing>>
  >({});
  const [partUploadStates, setPartUploadStates] = useState<
    Record<string, boolean>
  >({});

  // Components custom drawings state: { [component_id]: { [drawing_id_or_title]: { document, fileName } } }
  const [componentDrawings, setComponentDrawings] = useState<
    Record<string, Record<string, UploadedCustomDrawing>>
  >({});
  const [componentUploadStates, setComponentUploadStates] = useState<
    Record<string, boolean>
  >({});

  // Factory Assembled drawings state: { [product_id]: { document, fileName } }
  const [assemblyDrawings, setAssemblyDrawings] = useState<
    Record<string, UploadedCustomDrawing>
  >({});
  const [assemblyUploadStates, setAssemblyUploadStates] = useState<
    Record<string, boolean>
  >({});

  const [saving, setSaving] = useState(false);

  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (variantsList && variantsList.length > 0) {
      variantsList.forEach((item: any) => {
        setAllocations((prev) => ({
          ...prev,
          [item?.variant_id]: [
            { _id: uuidv4(), qty: item?.quantity, files: [] },
          ],
        }));
      });
    }
  }, [variantsList]);

  // Variant allocation handlers
  const handleUpdateRow = (
    variantId: string,
    groupId: string,
    key: string,
    value: any,
  ) => {
    setAllocations((prev) => ({
      ...prev,
      [variantId]: prev[variantId]?.map((grpItem) => {
        if (grpItem?._id === groupId) {
          return {
            ...grpItem,
            [key]: value,
          };
        }
        return grpItem;
      }),
    }));
  };

  const splitRow = (variantId: string, groupId: string) => {
    const currentGroup = allocations[variantId]?.find(
      (grpItem) => grpItem?._id === groupId,
    );

    if (currentGroup) {
      const mainQty = currentGroup?.qty;
      const splitQty = Math.ceil(mainQty / 2);
      const splitQty2 = mainQty - splitQty;

      const newGroup = { _id: uuidv4(), qty: splitQty2, files: [] };
      const updatedGroups = [
        ...allocations[variantId]?.flatMap((grpItem: DesignGroup) => {
          if (grpItem?._id === groupId) {
            return [
              {
                ...grpItem,
                qty: splitQty,
              },
              newGroup,
            ];
          } else {
            return grpItem;
          }
        }),
      ];
      setAllocations((prev) => ({
        ...prev,
        [variantId]: updatedGroups,
      }));
    }
  };

  const removeGroup = (variantId: string, groupId: string) => {
    setAllocations((prev) => ({
      ...prev,
      [variantId]: prev[variantId]?.filter(
        (grpItem) => grpItem?._id !== groupId,
      ),
    }));
  };

  const removeFile = (variantId: string, groupId: string, designId: string) => {
    const groupDesigns: File[] =
      allocations[variantId]?.find((dsItem) => dsItem?._id === groupId)
        ?.files || [];

    if (groupDesigns?.length > 0) {
      const updatedDesigns = groupDesigns?.filter(
        (item) => item._id !== designId,
      );
      handleUpdateRow(variantId, groupId, "files", updatedDesigns);
    }
  };

  const handleUploadDesignSuccess = (
    variantId: string,
    groupId: string,
    key: string,
    fileName: string,
  ) => {
    setUploadStates((prev) => {
      const base = prev?.[groupId] ?? 1;
      return {
        ...prev,
        [groupId]: Math.max(0, base - 1),
      };
    });
    setAllocations((prev) => ({
      ...prev,
      [variantId]: prev[variantId]?.map((grpItem) => {
        const files = grpItem?.files || [];
        if (grpItem?._id === groupId) {
          return {
            ...grpItem,
            files: [
              ...files,
              { _id: uuidv4(), fileName: fileName, key: key },
            ],
          };
        } else {
          return grpItem;
        }
      }),
    }));
  };

  const addDesignVersion = (variantId: string, remainingQty: number) => {
    const newSection: DesignGroup = {
      _id: uuidv4(),
      files: [],
      qty: remainingQty,
    };
    setAllocations((prev) => ({
      ...prev,
      [variantId]: [...(prev[variantId] || []), newSection],
    }));
  };

  // Part drawing handlers
  const handlePartUploadSuccess = (
    partId: string,
    drawingKey: string,
    key: string,
    fileName: string,
  ) => {
    setPartUploadStates((prev) => ({ ...prev, [`${partId}_${drawingKey}`]: false }));
    setPartDrawings((prev) => ({
      ...prev,
      [partId]: {
        ...(prev[partId] || {}),
        [drawingKey]: { document: key, fileName },
      },
    }));
  };

  const handleRemovePartDrawing = (partId: string, drawingKey: string) => {
    setPartDrawings((prev) => {
      const current = { ...(prev[partId] || {}) };
      delete current[drawingKey];
      return { ...prev, [partId]: current };
    });
  };

  // Component drawing handlers
  const handleComponentUploadSuccess = (
    componentId: string,
    drawingKey: string,
    key: string,
    fileName: string,
  ) => {
    setComponentUploadStates((prev) => ({
      ...prev,
      [`${componentId}_${drawingKey}`]: false,
    }));
    setComponentDrawings((prev) => ({
      ...prev,
      [componentId]: {
        ...(prev[componentId] || {}),
        [drawingKey]: { document: key, fileName },
      },
    }));
  };

  const handleRemoveComponentDrawing = (
    componentId: string,
    drawingKey: string,
  ) => {
    setComponentDrawings((prev) => {
      const current = { ...(prev[componentId] || {}) };
      delete current[drawingKey];
      return { ...prev, [componentId]: current };
    });
  };

  // Assembly drawing handlers
  const handleAssemblyUploadSuccess = (
    itemKey: string,
    key: string,
    fileName: string,
  ) => {
    setAssemblyUploadStates((prev) => ({
      ...prev,
      [itemKey]: false,
    }));
    setAssemblyDrawings((prev) => ({
      ...prev,
      [itemKey]: { document: key, fileName },
    }));
  };

  const handleRemoveAssemblyDrawing = (itemKey: string) => {
    setAssemblyDrawings((prev) => {
      const current = { ...prev };
      delete current[itemKey];
      return current;
    });
  };

  const submit = () => {
    const payload: any = {
      quote_id: quotationId,
    };

    if (variantsList && variantsList.length > 0) {
      payload.variant_drawing = variantsList.map((item: any) => ({
        variant_id: item?.variant_id,
        drawings: allocations[item?.variant_id]?.map((grpItem) => ({
          qty: grpItem?.qty,
          drawing_image: grpItem?.files?.map((file) => file.key),
        })),
      }));
    }

    if (partsList && partsList.length > 0) {
      payload.parts_drawing = partsList
        .map((part: any) => {
          const partId = part.part_id;
          const drws = (part.drawings || [])
            .map((d: any) => {
              const dKey = d._id || d.title;
              const uploaded = partDrawings[partId]?.[dKey];
              return {
                _id: d._id,
                title: d.title,
                document: uploaded?.document || null,
              };
            })
            .filter((d: any) => d.document);
          return {
            part_id: partId,
            drawings: drws,
          };
        })
        .filter((p: any) => p.drawings.length > 0);
    }

    if (componentsList && componentsList.length > 0) {
      payload.components_drawing = componentsList
        .map((comp: any) => {
          const compId = comp.component_id;
          const drws = (comp.drawings || [])
            .map((d: any) => {
              const dKey = d._id || d.title;
              const uploaded = componentDrawings[compId]?.[dKey];
              return {
                _id: d._id,
                title: d.title,
                document: uploaded?.document || null,
              };
            })
            .filter((d: any) => d.document);
          return {
            component_id: compId,
            drawings: drws,
          };
        })
        .filter((c: any) => c.drawings.length > 0);
    }

    if (assemblyList && assemblyList.length > 0) {
      payload.assembly_drawing = assemblyList
        .map((item: any) => {
          const itemKey = item.variant_id ? `${item.product_id}_${item.variant_id}` : item.product_id;
          const uploaded = assemblyDrawings[itemKey];
          return {
            product_id: item.product_id,
            variant_id: item.variant_id || null,
            assembly_drawing_doc:
              uploaded?.document || item?.assembly_drawing_doc || null,
          };
        })
        .filter((ad: any) => ad.assembly_drawing_doc);
    }

    onSubmit?.(payload, setSaving);
  };

  const areVariantsValid =
    !variantsList ||
    variantsList.length === 0 ||
    variantsList.every((item: any) => {
      const requiredQuantity = item?.quantity;
      const allocatedQuantity = allocations[item?.variant_id]?.reduce(
        (acc, grpItem) => acc + grpItem?.qty,
        0,
      );
      const areAllQuantitesAllocated = requiredQuantity === allocatedQuantity;
      const doesAllGroupsHasDesigns = allocations[item?.variant_id]?.every(
        (grpItem) => grpItem?.files?.length > 0,
      );
      return areAllQuantitesAllocated && doesAllGroupsHasDesigns;
    });

  const arePartsValid =
    !partsList ||
    partsList.length === 0 ||
    partsList.every((part: any) => {
      return (part.drawings || []).every((d: any) => {
        const dKey = d._id || d.title;
        const uploaded = partDrawings[part.part_id]?.[dKey];
        return !!uploaded?.document;
      });
    });

  const areComponentsValid =
    !componentsList ||
    componentsList.length === 0 ||
    componentsList.every((comp: any) => {
      return (comp.drawings || []).every((d: any) => {
        const dKey = d._id || d.title;
        const uploaded = componentDrawings[comp.component_id]?.[dKey];
        return !!uploaded?.document;
      });
    });

  const areAssemblyDrawingsValid =
    !assemblyList ||
    assemblyList.length === 0 ||
    assemblyList.every((item: any) => {
      const itemKey = item.variant_id ? `${item.product_id}_${item.variant_id}` : item.product_id;
      const uploaded = assemblyDrawings[itemKey];
      return !!(uploaded?.document || item?.assembly_drawing_doc);
    });

  const isFormValid =
    areVariantsValid &&
    arePartsValid &&
    areComponentsValid &&
    areAssemblyDrawingsValid;

  const modalTitle =
    (partsList.length > 0 || componentsList.length > 0 || assemblyList.length > 0) &&
    variantsList.length > 0
      ? "Add Required Drawings for Order"
      : partsList.length > 0 || componentsList.length > 0
      ? "Add Required Drawings for Parts & Components"
      : assemblyList.length > 0
      ? "Add Factory Assembled Drawings"
      : "Add Drawings for Variants";

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={
        <div className="d-flex align-items-center justify-content-center gap-3 gap-sm-2">
          <Button
            className="btn btn-lg btn-outline-secondary px-4 h-auto text-uppercase"
            style={{ minWidth: "140px" }}
            onClick={() => onClose?.()}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-lg btn-primary px-4 h-auto text-uppercase"
            style={{ minWidth: "140px" }}
            onClick={() => submit()}
            disabled={!isFormValid}
            loading={saving}
          >
            Save
          </Button>
        </div>
      }
      closeIcon={svg?.closeIcon}
      width={"70rem"}
      styles={{
        body: {
          maxHeight: "calc(100vh - 324px)",
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: "10px",
        },
      }}
      className="commonModalClass"
      title={
        <div className="text-center fs-4 fs-4-mobile fw-bolder lh-m mb-4">
          {modalTitle}
        </div>
      }
    >
      <div className="d-flex flex-column gap-4">
        {/* ── 1. Variants Requiring Drawing Section ── */}
        {variantsList.length > 0 && (
          <div className="d-flex flex-column gap-3">
            {(partsList.length > 0 || componentsList.length > 0 || assemblyList.length > 0) && (
              <h5 className="fw-bold text-dark border-bottom pb-2 mb-2">
                Variant Drawings
              </h5>
            )}
            {variantsList.map((item: any) => {
              const variantId = item?.variant_id;
              const designGroups = allocations[variantId] || [];
              const allocatedQty = designGroups.reduce(
                (acc, grpItem) => acc + grpItem?.qty,
                0,
              );
              const remainingQty = item?.quantity - allocatedQty;

              return (
                <div key={variantId || item?._id} className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                    <h6 className="fw-bold mb-1 fs-6 fs-6-mob">
                      {item?.variant_name}
                    </h6>
                    <div
                      className={`badge ${
                        remainingQty === 0
                          ? "bg-success"
                          : "bg-warning text-dark"
                      } p-2 px-3 rounded-pill fs-6`}
                    >
                      Remaining: {remainingQty}
                    </div>
                  </div>

                  {designGroups.map((designGroup: DesignGroup) => {
                    const groupId = designGroup?._id;
                    const files = designGroup?.files || [];
                    const uploadNumbers = uploadStates[groupId];

                    return (
                      <div key={groupId} className="d-flex flex-column gap-y-3">
                        <div className="card bg-light rounded border mx-0">
                          <div className="card-body p-3 d-flex flex-column gap-2">
                            <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-0">
                              <div className="d-flex flex-row gap-2 align-items-center">
                                <label className="small fw-bold mb-1">
                                  Qty
                                </label>
                                <div className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput">
                                  <Button
                                    type="text"
                                    icon={<MinusOutlined />}
                                    className="btn btn-primary addRemoveBtn"
                                    onClick={() =>
                                      handleUpdateRow(
                                        variantId,
                                        groupId,
                                        "qty",
                                        Math.max(1, designGroup.qty - 1),
                                      )
                                    }
                                    disabled={designGroup.qty <= 1}
                                  />
                                  <InputNumber
                                    value={designGroup?.qty}
                                    min={1}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    onChange={(val) => {
                                      if (!val) return;
                                      const maxAllocatableQty =
                                        designGroup?.qty + remainingQty;

                                      if (
                                        val >= 1 &&
                                        val <= maxAllocatableQty
                                      ) {
                                        handleUpdateRow(
                                          variantId,
                                          groupId,
                                          "qty",
                                          val,
                                        );
                                      }
                                    }}
                                    className="border-0 text-center w-100 shadow-none"
                                    controls={false}
                                  />
                                  <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    className="btn btn-primary addRemoveBtn"
                                    onClick={() => {
                                      handleUpdateRow(
                                        variantId,
                                        groupId,
                                        "qty",
                                        Math.min(
                                          item?.quantity,
                                          designGroup?.qty + 1,
                                        ),
                                      );
                                    }}
                                    disabled={
                                      remainingQty <= 0 ||
                                      designGroup?.qty >= item?.quantity
                                    }
                                  />
                                </div>
                              </div>
                              <div className="ms-auto d-flex">
                                {designGroup?.qty > 1 && (
                                  <Button
                                    type="text"
                                    size="small"
                                    className="text-primary fw-bold text-decoration-underline"
                                    onClick={() =>
                                      splitRow(variantId, groupId)
                                    }
                                  >
                                    Split
                                  </Button>
                                )}
                                {designGroups.length > 1 && (
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                      removeGroup(variantId, groupId)
                                    }
                                  />
                                )}
                              </div>
                            </div>

                            <div>
                              <S3FileUploader
                                folder="drawings"
                                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                multiple={true}
                                onUploadStart={() => {
                                  setUploadStates((prev) => {
                                    const base = prev?.[groupId] ?? 0;
                                    return {
                                      ...prev,
                                      [groupId]: base + 1,
                                    };
                                  });
                                }}
                                onError={() => {
                                  setUploadStates((prev) => {
                                    const base = prev?.[groupId] ?? 1;
                                    return {
                                      ...prev,
                                      [groupId]: Math.max(0, base - 1),
                                    };
                                  });
                                }}
                                onSuccess={(key, fileName) =>
                                  handleUploadDesignSuccess(
                                    variantId,
                                    groupId,
                                    key,
                                    fileName,
                                  )
                                }
                                className="bg-white rounded-3 py-1"
                                style={{ border: "1.5px dashed #999" }}
                              >
                                {uploadNumbers ? (
                                  <div className="py-2 d-flex justify-content-center w-100">
                                    <Spin tip="Uploading design..." />
                                  </div>
                                ) : (
                                  <div className="d-flex align-items-center gap-3 ant-upload-text fw-semibold">
                                    <div>
                                      <span className="svg-48 ant-upload-drag-icon">
                                        {svg?.importIcon}
                                      </span>
                                    </div>
                                    <div>Drag & Drop File</div>
                                    <Button className="ms-auto">
                                      Browse Files
                                    </Button>
                                  </div>
                                )}
                              </S3FileUploader>
                            </div>

                            <div className="d-flex flex-column gap-2">
                              {files.map((file, index) => (
                                <div
                                  key={file._id}
                                  className="card border-light-subtle rounded-3 shadow-none overflow-hidden"
                                >
                                  <div className="card-body py-1 px-2 d-flex flex-sm-row flex-column justify-content-between align-items-sm-center">
                                    <span className="text-primary fw-semibold fs-6 text-break">
                                      {getFileNameFromUrl(file.fileName || file.key)}
                                    </span>

                                    <div className="d-flex align-items-center gap-3 mt-sm-0 mt-2">
                                      <Button
                                        type="link"
                                        className="text-primary fw-bold p-0 text-decoration-underline fs-6 me-sm-0 me-auto"
                                        onClick={() => {
                                          setPreviewFiles(
                                            files?.map((itm) =>
                                              getFullAwsUrl(itm?.key),
                                            ),
                                          );
                                          setPreviewIndex(index);
                                          setPreviewModalOpen(true);
                                        }}
                                      >
                                        View
                                      </Button>
                                      <Button
                                        type="text"
                                        icon={<DeleteOutlined />}
                                        danger
                                        onClick={() =>
                                          removeFile(
                                            variantId,
                                            groupId,
                                            file?._id,
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {remainingQty > 0 && (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => addDesignVersion(variantId, remainingQty)}
                    >
                      Add Design Version
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── 2. Parts Requiring Custom Drawing Section ── */}
        {partsList.length > 0 && (
          <div className="d-flex flex-column gap-3">
            <h5 className="fw-bold text-dark border-bottom pb-2 mb-2">
              Part Drawings
            </h5>
            {partsList.map((part: any, pIdx: number) => {
              const partId = part.part_id;
              const drawings = part.drawings || [];

              return (
                <div
                  key={partId || pIdx}
                  className="card border rounded-3 p-3 bg-light d-flex flex-column gap-3"
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0 text-dark fs-6">
                      {part.part_name || `Part ${pIdx + 1}`}
                    </h6>
                    <span className="badge bg-secondary text-white px-2 py-1 fs-12">
                      {drawings.length} Custom Drawing{drawings.length > 1 ? "s" : ""} Required
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    {drawings.map((drawing: any, dIdx: number) => {
                      const dKey = drawing._id || drawing.title || String(dIdx);
                      const uploaded = partDrawings[partId]?.[dKey];
                      const isUploading =
                        partUploadStates[`${partId}_${dKey}`] || false;
                      const deptNames = Array.isArray(drawing.departments)
                        ? drawing.departments
                            .map((d: any) => d?.name || d)
                            .filter(Boolean)
                            .join(", ")
                        : "";

                      return (
                        <div
                          key={dKey}
                          className="card bg-white rounded-3 border p-3 shadow-none"
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                            <div>
                              <div className="fw-semibold text-dark fs-14">
                                {drawing.title || `Drawing ${dIdx + 1}`}
                              </div>
                              {deptNames && (
                                <small className="text-muted fs-12">
                                  Departments: {deptNames}
                                </small>
                              )}
                            </div>
                            {uploaded?.document ? (
                              <span className="badge bg-success px-2 py-1 fs-12">
                                Uploaded
                              </span>
                            ) : (
                              <span className="badge bg-danger px-2 py-1 fs-12">
                                Required
                              </span>
                            )}
                          </div>

                          {uploaded?.document ? (
                            <div className="d-flex align-items-center justify-content-between p-2 bg-light border rounded-2">
                              <div className="d-flex align-items-center gap-2 text-truncate me-3">
                                <PaperClipOutlined className="text-primary fs-16" />
                                <span
                                  className="fw-semibold text-dark text-truncate fs-13"
                                  title={getFileNameFromUrl(
                                    uploaded.fileName || uploaded.document,
                                  )}
                                >
                                  {getFileNameFromUrl(
                                    uploaded.fileName || uploaded.document,
                                  )}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <Button
                                  type="text"
                                  icon={svg?.viewIcon}
                                  onClick={() => {
                                    setPreviewFiles([
                                      getFullAwsUrl(uploaded.document),
                                    ]);
                                    setPreviewIndex(0);
                                    setPreviewModalOpen(true);
                                  }}
                                  title="View Drawing"
                                  className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                                />
                                <Button
                                  type="text"
                                  icon={svg?.deleteBtn}
                                  onClick={() =>
                                    handleRemovePartDrawing(partId, dKey)
                                  }
                                  title="Remove Drawing"
                                  className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                                />
                              </div>
                            </div>
                          ) : (
                            <S3FileUploader
                              folder="part_component_drawings"
                              accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.svg,.doc,.docx"
                              multiple={false}
                              onUploadStart={() =>
                                setPartUploadStates((prev) => ({
                                  ...prev,
                                  [`${partId}_${dKey}`]: true,
                                }))
                              }
                              onError={() =>
                                setPartUploadStates((prev) => ({
                                  ...prev,
                                  [`${partId}_${dKey}`]: false,
                                }))
                              }
                              onSuccess={(key, fileName) =>
                                handlePartUploadSuccess(
                                  partId,
                                  dKey,
                                  key,
                                  fileName,
                                )
                              }
                              className="bg-white rounded-3 py-2 text-center"
                              style={{ border: "1.5px dashed #bbb" }}
                            >
                              {isUploading ? (
                                <div className="py-2">
                                  <Spin tip="Uploading drawing file..." />
                                </div>
                              ) : (
                                <div className="d-flex align-items-center justify-content-between px-3 py-1 cursor-pointer">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="svg-32">
                                      {svg?.importIcon}
                                    </span>
                                    <span className="fs-13 fw-semibold text-dark">
                                      Upload Drawing File (PDF, DWG, DXF, PNG, JPG)
                                    </span>
                                  </div>
                                  <Button size="small">Browse</Button>
                                </div>
                              )}
                            </S3FileUploader>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── 3. Components Requiring Custom Drawing Section ── */}
        {componentsList.length > 0 && (
          <div className="d-flex flex-column gap-3">
            <h5 className="fw-bold text-dark border-bottom pb-2 mb-2">
              Component Drawings
            </h5>
            {componentsList.map((comp: any, cIdx: number) => {
              const compId = comp.component_id;
              const drawings = comp.drawings || [];

              return (
                <div
                  key={compId || cIdx}
                  className="card border rounded-3 p-3 bg-light d-flex flex-column gap-3"
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0 text-dark fs-6">
                      {comp.component_name || `Component ${cIdx + 1}`}
                    </h6>
                    <span className="badge bg-secondary text-white px-2 py-1 fs-12">
                      {drawings.length} Custom Drawing{drawings.length > 1 ? "s" : ""} Required
                    </span>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    {drawings.map((drawing: any, dIdx: number) => {
                      const dKey = drawing._id || drawing.title || String(dIdx);
                      const uploaded = componentDrawings[compId]?.[dKey];
                      const isUploading =
                        componentUploadStates[`${compId}_${dKey}`] || false;
                      const deptNames = Array.isArray(drawing.departments)
                        ? drawing.departments
                            .map((d: any) => d?.name || d)
                            .filter(Boolean)
                            .join(", ")
                        : "";

                      return (
                        <div
                          key={dKey}
                          className="card bg-white rounded-3 border p-3 shadow-none"
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                            <div>
                              <div className="fw-semibold text-dark fs-14">
                                {drawing.title || `Drawing ${dIdx + 1}`}
                              </div>
                              {deptNames && (
                                <small className="text-muted fs-12">
                                  Departments: {deptNames}
                                </small>
                              )}
                            </div>
                            {uploaded?.document ? (
                              <span className="badge bg-success px-2 py-1 fs-12">
                                Uploaded
                              </span>
                            ) : (
                              <span className="badge bg-danger px-2 py-1 fs-12">
                                Required
                              </span>
                            )}
                          </div>

                          {uploaded?.document ? (
                            <div className="d-flex align-items-center justify-content-between p-2 bg-light border rounded-2">
                              <div className="d-flex align-items-center gap-2 text-truncate me-3">
                                <PaperClipOutlined className="text-primary fs-16" />
                                <span
                                  className="fw-semibold text-dark text-truncate fs-13"
                                  title={getFileNameFromUrl(
                                    uploaded.fileName || uploaded.document,
                                  )}
                                >
                                  {getFileNameFromUrl(
                                    uploaded.fileName || uploaded.document,
                                  )}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <Button
                                  type="text"
                                  icon={svg?.viewIcon}
                                  onClick={() => {
                                    setPreviewFiles([
                                      getFullAwsUrl(uploaded.document),
                                    ]);
                                    setPreviewIndex(0);
                                    setPreviewModalOpen(true);
                                  }}
                                  title="View Drawing"
                                  className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                                />
                                <Button
                                  type="text"
                                  icon={svg?.deleteBtn}
                                  onClick={() =>
                                    handleRemoveComponentDrawing(compId, dKey)
                                  }
                                  title="Remove Drawing"
                                  className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                                />
                              </div>
                            </div>
                          ) : (
                            <S3FileUploader
                              folder="part_component_drawings"
                              accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.svg,.doc,.docx"
                              multiple={false}
                              onUploadStart={() =>
                                setComponentUploadStates((prev) => ({
                                  ...prev,
                                  [`${compId}_${dKey}`]: true,
                                }))
                              }
                              onError={() =>
                                setComponentUploadStates((prev) => ({
                                  ...prev,
                                  [`${compId}_${dKey}`]: false,
                                }))
                              }
                              onSuccess={(key, fileName) =>
                                handleComponentUploadSuccess(
                                  compId,
                                  dKey,
                                  key,
                                  fileName,
                                )
                              }
                              className="bg-white rounded-3 py-2 text-center"
                              style={{ border: "1.5px dashed #bbb" }}
                            >
                              {isUploading ? (
                                <div className="py-2">
                                  <Spin tip="Uploading drawing file..." />
                                </div>
                              ) : (
                                <div className="d-flex align-items-center justify-content-between px-3 py-1 cursor-pointer">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="svg-32">
                                      {svg?.importIcon}
                                    </span>
                                    <span className="fs-13 fw-semibold text-dark">
                                      Upload Drawing File (PDF, DWG, DXF, PNG, JPG)
                                    </span>
                                  </div>
                                  <Button size="small">Browse</Button>
                                </div>
                              )}
                            </S3FileUploader>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── 4. Factory Assembled Drawings Section ── */}
        {assemblyList.length > 0 && (
          <div className="d-flex flex-column gap-3">
            {(variantsList.length > 0 || partsList.length > 0 || componentsList.length > 0) && (
              <h5 className="fw-bold text-dark border-bottom pb-2 mb-2">
                Factory Assembled Drawings
              </h5>
            )}
            {assemblyList.map((item: any) => {
              const itemKey = item.variant_id ? `${item.product_id}_${item.variant_id}` : item.product_id;
              const uploaded = assemblyDrawings[itemKey];
              const doc = uploaded?.document || item.assembly_drawing_doc;
              const fileName = uploaded?.fileName || getFileNameFromUrl(doc || "");
              const isUploading = !!assemblyUploadStates[itemKey];

              return (
                <div
                  key={itemKey}
                  className="d-flex flex-column gap-2 p-3 border rounded-3 bg-white shadow-sm"
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <span className="fw-semibold text-dark fs-14">
                        {item.variant_name || item.product_name}
                      </span>
                      <span className="text-muted ms-2 fs-12">
                        (Factory Assembled Drawing)
                      </span>
                    </div>
                    {doc ? (
                      <span className="badge bg-success px-2 py-1 fs-12">
                        Uploaded
                      </span>
                    ) : (
                      <span className="badge bg-danger px-2 py-1 fs-12">
                        Required
                      </span>
                    )}
                  </div>

                  {doc ? (
                    <div className="d-flex align-items-center justify-content-between p-2 bg-light border rounded-2">
                      <div className="d-flex align-items-center gap-2 text-truncate me-3">
                        <PaperClipOutlined className="text-primary fs-16" />
                        <span
                          className="fw-semibold text-dark text-truncate fs-13"
                          title={fileName}
                        >
                          {fileName}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <Button
                          type="text"
                          icon={svg?.viewIcon}
                          onClick={() => {
                            setPreviewFiles([getFullAwsUrl(doc)]);
                            setPreviewIndex(0);
                            setPreviewModalOpen(true);
                          }}
                          title="View Drawing"
                          className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                        />
                        <Button
                          type="text"
                          icon={svg?.deleteBtn}
                          onClick={() => handleRemoveAssemblyDrawing(itemKey)}
                          title="Remove Drawing"
                          className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                        />
                      </div>
                    </div>
                  ) : (
                    <S3FileUploader
                      folder="order_drawings"
                      accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.svg,.doc,.docx"
                      multiple={false}
                      onUploadStart={() =>
                        setAssemblyUploadStates((prev) => ({
                          ...prev,
                          [itemKey]: true,
                        }))
                      }
                      onError={() =>
                        setAssemblyUploadStates((prev) => ({
                          ...prev,
                          [itemKey]: false,
                        }))
                      }
                      onSuccess={(key, fileName) =>
                        handleAssemblyUploadSuccess(
                          itemKey,
                          key,
                          fileName || getFileNameFromUrl(key),
                        )
                      }
                      className="bg-white rounded-3 py-2 text-center"
                      style={{ border: "1.5px dashed #bbb" }}
                    >
                      {isUploading ? (
                        <div className="py-2">
                          <Spin tip="Uploading drawing file..." />
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-between px-3 py-1 cursor-pointer">
                          <div className="d-flex align-items-center gap-2">
                            <span className="svg-32">{svg?.importIcon}</span>
                            <span className="fs-13 fw-semibold text-dark">
                              Upload Factory Assembled Drawing (PDF, DWG, DXF, PNG, JPG)
                            </span>
                          </div>
                          <Button size="small">Browse</Button>
                        </div>
                      )}
                    </S3FileUploader>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {previewModalOpen && previewFiles && (
        <CommonFilePreviewModal
          open={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFiles(null);
            setPreviewIndex(0);
          }}
          files={previewFiles}
          activeIndex={previewIndex}
          onActiveIndexChange={setPreviewIndex}
        />
      )}
    </Modal>
  );
};

export default ConfigureDrawingsModal;
